import { useExpertSelection } from "@/components/Expert/selection/ExpertSelectionContext";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { getInitialChatName } from "@/lib/chat-name";
import { chatLibraryFileControllerAttachFile } from "@/lib/gen-api";
import { useEffect, useRef, useState } from "react";
import {
  Capabilities,
  useAssistContext,
} from "../components/Assist/AssistContext";
import { useChatsData } from "../data/use-chats-data";
import { useMessagesData } from "../data/use-messages-data";
import { useArtifactUuid } from "../hooks/use-artifact-uuid";

/**
 * This action can be used to add a message to a chat or to start a new chat. This wrapper is
 * preferred over going to the data hook directly as it takes care of cases such as the chat not existing
 * or file uploads.
 */
export function useCreateAssistMessageAction() {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const {
    modelProfileUuid,
    activeChatUuid,
    setActiveChatUuid,
    userPrompt,
    setUserPrompt,
    capabilities,
    setMessageStage,
    forwardChatUuid,
  } = useAssistContext();
  const { selectExperts, linkExperts, kitType, kitExperts } =
    useExpertSelection();
  const { createChat } = useChatsData(groupUuid, kitUuid);
  const documentUuid = useArtifactUuid("documentUuid");
  const [isUploading, setIsUploading] = useState(false);
  const {
    createMessage,
    createMessageStage,
    cancelMessage: originalCancelMessage,
  } = useMessagesData(groupUuid, kitUuid, activeChatUuid);
  const lastUserPromptRef = useRef("");
  const submissionRef = useRef<Promise<{ chatUuid: string }> | null>(null);

  useEffect(() => {
    if (isUploading) {
      setMessageStage("uploading");
    } else {
      setMessageStage(createMessageStage);
    }
  }, [isUploading, createMessageStage, setMessageStage]);

  const restoreUserPrompt = () => {
    setUserPrompt(lastUserPromptRef.current);
  };

  const cancelMessage = () => {
    originalCancelMessage();
    restoreUserPrompt();
  };

  const createSubmission = async (content: {
    expertUuids: Set<string>;
    skillUuid?: string;
    fileUuids?: string[];
    capabilityOverrides?: Partial<Capabilities>;
  }) => {
    lastUserPromptRef.current = userPrompt;
    setUserPrompt("");

    let chatUuid: string | undefined | null = activeChatUuid;

    if (!chatUuid || forwardChatUuid) {
      // The input stays idle until the stream starts. Hold it before these
      // awaits so a second send cannot start another chat.
      setMessageStage("uploading");
      try {
        // KitExpertsGuard rejects a chat whose expert is not already on the
        // kit. An expert reached through a linked collection already counts,
        // and linking them again requires an expert grant the collection
        // does not give.
        const expertUuidsToLink = [...content.expertUuids].filter(
          (expertUuid) =>
            !kitExperts.some((expert) => expert.uuid === expertUuid),
        );
        if (
          (kitType === "project" || kitType === "expert") &&
          expertUuidsToLink.length > 0
        ) {
          await linkExperts(new Set(expertUuidsToLink));
        }

        chatUuid = await createChat({
          name: forwardChatUuid ? undefined : getInitialChatName(),
          expertUuids: Array.from(content.expertUuids),
          transcriptChatUuid: forwardChatUuid ?? undefined,
        });

        if (!chatUuid) {
          throw new Error("could not create a new chat");
        }

        // Switching chats clears staged files, so attach them before activation.
        if (content.fileUuids?.length && groupUuid && kitUuid) {
          await Promise.all(
            content.fileUuids.map((fileUuid) =>
              chatLibraryFileControllerAttachFile({
                path: { fileUuid, chatUuid: chatUuid! },
                throwOnError: true,
              }),
            ),
          );
        }
        setActiveChatUuid(chatUuid);
      } catch (error) {
        setMessageStage(createMessageStage);
        restoreUserPrompt();
        throw error;
      }
    }

    if (!chatUuid) {
      setMessageStage(createMessageStage);
      restoreUserPrompt();
      throw new Error("could not create a new chat");
    }

    selectExperts(content.expertUuids);

    const effectiveCapabilities = content.capabilityOverrides
      ? { ...capabilities, ...content.capabilityOverrides }
      : capabilities;

    createMessage({
      prompt: userPrompt,
      skillUuid: content.skillUuid,
      documentUuid: documentUuid ?? undefined,
      capabilities: effectiveCapabilities,
      overwriteChatUuid: chatUuid,
      onError: () => {
        restoreUserPrompt();
      },
      modelProfileUuid: modelProfileUuid,
    });

    setIsUploading(false);
    return { chatUuid };
  };

  return {
    cancelMessage,
    restoreUserPrompt,

    async createMessage(content: {
      expertUuids: Set<string>;
      skillUuid?: string;
      fileUuids?: string[];
      capabilityOverrides?: Partial<Capabilities>;
    }) {
      if (submissionRef.current) {
        return submissionRef.current;
      }

      const submission = createSubmission(content);
      submissionRef.current = submission;
      try {
        return await submission;
      } finally {
        submissionRef.current = null;
      }
    },
  };
}
